export interface TranscriptionWord {
  /** Start time in milliseconds. */
  start: number;
  /** End time in milliseconds. */
  end: number;
  /** Word text — includes trailing punctuation (e.g. "Sa.", "we", "this."). */
  text: string;
}

export interface TranscriptionSegment {
  /** Speaker label (e.g. "A", "B"). Free-form string; we don't assume a fixed set. */
  speaker: string;
  /** Start of the segment in milliseconds. */
  start: number;
  /** End of the segment in milliseconds. */
  end: number;
  /** Full segment text — duplicates the joined word text but kept for download fidelity. */
  text: string;
  /** Per-word timing. */
  words: TranscriptionWord[];
}

export type Transcription = TranscriptionSegment[];

/** Lifecycle of an async transcription load. */
export type TranscriptionStatus = "loading" | "ready" | "error";

export interface ActiveWordPosition {
  segmentIndex: number;
  wordIndex: number;
  /**
   * `active` — currentTime is within [word.start, word.end], the word is
   * currently being spoken.
   * `trailing` — the most recently spoken word, but currentTime has moved
   * past it into a silence gap (between words, between segments, or after
   * the last word). Render dimmed so the user still sees the most recent
   * context.
   */
  state: "active" | "trailing";
}

/**
 * Find the word being spoken at `currentTimeMs`, OR the most recently
 * spoken word if we're in a silence gap. Returns null only when we're
 * before the first word of the transcription (nothing has been said yet).
 *
 * Linear scan — 36 segments × ~20 words is fine; not worth a binary search
 * until the data gets much larger.
 */
export function findActiveWord(
  transcription: Transcription,
  currentTimeMs: number,
): ActiveWordPosition | null {
  // Track the last word we've passed so we can surface it as "trailing" when
  // we're in a gap (silence between segments, or before the next segment
  // starts).
  let trailing: { segmentIndex: number; wordIndex: number } | null = null;

  for (let s = 0; s < transcription.length; s++) {
    const segment = transcription[s];

    if (currentTimeMs < segment.start) {
      // Haven't reached this segment yet — fall out and return trailing.
      break;
    }

    if (currentTimeMs <= segment.end) {
      // Inside this segment — find the most recent word start.
      for (let w = segment.words.length - 1; w >= 0; w--) {
        if (currentTimeMs >= segment.words[w].start) {
          const isActive = currentTimeMs <= segment.words[w].end;
          return {
            segmentIndex: s,
            wordIndex: w,
            state: isActive ? "active" : "trailing",
          };
        }
      }
      // Inside segment but before its first word fires (silence at segment
      // start). Use the trailing from prior segments if available.
      break;
    }

    // Past this segment entirely — record its last word as a candidate
    // trailing, then keep looking.
    if (segment.words.length > 0) {
      trailing = { segmentIndex: s, wordIndex: segment.words.length - 1 };
    }
  }

  return trailing ? { ...trailing, state: "trailing" } : null;
}

/** Format a millisecond timestamp as "M:SS" for segment headers. */
export function formatSegmentTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export interface TranscriptionLine {
  /** Start time in ms — the first word's start. */
  startMs: number;
  /** Index of the first word in the parent segment's words array. */
  startWordIndex: number;
  /** Words that make up this line. */
  words: TranscriptionWord[];
}

export interface WordMatchRange {
  /** First word index in the parent segment's words array. */
  startWord: number;
  /** Last word index (inclusive). */
  endWord: number;
}

/**
 * Find all match ranges of `query` in a segment's joined word text. The
 * query is treated as a literal substring (case-insensitive); we map the
 * character match back to a `[startWord, endWord]` range so the renderer
 * can highlight contiguous word spans.
 *
 * Multi-word queries (e.g. "everything you're doing") work because we
 * search the joined text including spaces.
 */
export function findWordMatchesInSegment(
  words: TranscriptionWord[],
  query: string,
): WordMatchRange[] {
  if (!query.trim() || words.length === 0) return [];
  // Build the joined string + a parallel index mapping each character
  // back to its source word (or -1 for the spaces between words).
  let joined = "";
  const charToWord: number[] = [];
  for (let i = 0; i < words.length; i++) {
    if (i > 0) {
      joined += " ";
      charToWord.push(-1);
    }
    const start = joined.length;
    joined += words[i].text;
    for (let c = start; c < joined.length; c++) charToWord.push(i);
  }
  const lowerJoined = joined.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const matches: WordMatchRange[] = [];
  let idx = 0;
  while ((idx = lowerJoined.indexOf(lowerQuery, idx)) !== -1) {
    // Walk inward if the match boundary lands on a space (no word index).
    let startWord = charToWord[idx];
    let endWord = charToWord[idx + lowerQuery.length - 1];
    let s = idx;
    while (startWord === -1 && s < idx + lowerQuery.length) {
      s++;
      startWord = charToWord[s];
    }
    let e = idx + lowerQuery.length - 1;
    while (endWord === -1 && e > idx) {
      e--;
      endWord = charToWord[e];
    }
    if (startWord >= 0 && endWord >= 0 && endWord >= startWord) {
      matches.push({ startWord, endWord });
    }
    idx += Math.max(1, lowerQuery.length);
  }
  return matches;
}

/**
 * Split a segment's words into lines by sentence-ending punctuation. The
 * Figma shows each "thought" as its own line with a leading timestamp;
 * splitting on `.`/`!`/`?` is a good proxy without needing NLP.
 *
 * Returns at least one line even if there's no terminal punctuation.
 */
export function splitWordsIntoLines(
  words: TranscriptionWord[],
): TranscriptionLine[] {
  if (words.length === 0) return [];
  const lines: TranscriptionLine[] = [];
  let bucket: TranscriptionWord[] = [];
  let bucketStartIndex = 0;
  for (let i = 0; i < words.length; i++) {
    if (bucket.length === 0) bucketStartIndex = i;
    bucket.push(words[i]);
    if (/[.!?]$/.test(words[i].text)) {
      lines.push({
        startMs: bucket[0].start,
        startWordIndex: bucketStartIndex,
        words: bucket,
      });
      bucket = [];
    }
  }
  if (bucket.length > 0) {
    lines.push({
      startMs: bucket[0].start,
      startWordIndex: bucketStartIndex,
      words: bucket,
    });
  }
  return lines;
}
